// Shapes of the CRM integration API (/api/v1/crm, backend/src/modules/crm/http). Tokens and client secrets never
// come back from it: a connection says only whether it is authorized, a client only whether it has a secret.
import { ApiError, api } from '@/lib/api';
import i18n, { t } from '@/i18n';

export const CRM = '/v1/crm';

export interface Capability { key: string; supported: boolean; reason?: string }
export interface Provider {
  code: string; name: string; enabled: boolean; api_version: string; platform_client: boolean; callback_url: string | null;
  capabilities: Capability[]; default_objects: Record<string, string>;
}
export interface Settings {
  screen_pop: { enabled: boolean; remote_lookup: 'ALWAYS' | 'ON_LOCAL_MISS' | 'NEVER'; objects?: string[] };
  call_logging: { enabled: boolean; log_unanswered: boolean; log_internal: boolean; subject_template?: string };
  unknown_caller: { create: 'NEVER' | 'AGENT_CONFIRMS' | 'AUTO'; object?: 'LEAD' | 'CONTACT' };
  recording_url: { enabled: boolean; ttl_hours: number };
  webhooks: { enabled: boolean };
  batch_size: number; max_concurrency: number; rps: number; call_correlation_field: string | null; country: string | null; incremental_sync_sec: number;
}
export interface Connection {
  id: number; provider: string; name: string; environment: string; status: string; enabled: boolean; is_default_lookup: boolean;
  organization_id: string | null; instance_url: string | null; api_domain: string | null; api_version: string | null;
  authorized: boolean; authorized_at: string | null; token_expiry: string | null; granted_scopes: string[]; remote_user_id: string | null;
  last_success_at: string | null; last_error: { code: string | null; message: string | null; at: string | null };
  consecutive_failures: number; paused_until: string | null; quota: { used: number | null; limit: number | null; at: string | null };
  settings: Settings; capabilities: Capability[]; created_at: string | null;
}
export interface OAuthClient {
  id: number; provider: string; name: string; client_id: string; has_secret: boolean; scopes: string; use_pkce: boolean; enabled: boolean; redirect_uri: string;
  connections: number;
}
// mapping rows come as stored: flags are 0/1
export interface FieldMapping {
  id: number; local_field: string; remote_field: string; direction: string | null; transformation: string; is_required: number; is_match_key: number; enabled: number;
}
export interface ObjectMapping {
  id: number; local_entity: string; remote_object: string; sync_direction: string; conflict_policy: string; use_for_lookup: number; enabled: number;
  configuration_json: { list_id?: number | null; page_size?: number | null } | null; fields: FieldMapping[];
}
export interface TaskConfig { subject: string; due_from?: 'CALLBACK_TIME' | 'CALL_END'; due_offset_min?: number; assign_to?: 'AGENT' | 'RECORD_OWNER' | 'NONE'; priority?: 'LOW' | 'NORMAL' | 'HIGH' }
export interface DispositionMapping {
  id: number; campaign_id: number | null; disposition_code: string; action: string; remote_object: string | null; remote_field: string | null;
  remote_value: string | null; activity_result_value: string | null; enabled: number; task_config_json: TaskConfig | null;
}
export interface UserMapping { id: number; user_id: number; username: string; name: string; remote_user_id: string; remote_user_name: string | null; remote_email: string | null; matched_by: string }
export interface CrmUser { id: string; name: string; email?: string | null; active: boolean }
export interface RecordCard {
  object: string; id: string; type: string; name: string; company: string | null; email: string | null; phones: { field: string; number: string }[];
  url: string | null; owner_id: string | null; modified_at: string | null; source: string;
}

// a CRM error: the code in the portal's language, and the CRM module's own (English, already redacted) detail
export function crmErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = `err.${e.code}`;
    const head = i18n.exists(key) ? t(key) : e.code.replace(/_/g, ' ').toLowerCase();
    const msg = typeof e.body?.message === 'string' ? e.body.message : '';
    return msg && msg !== head ? `${head}: ${msg}` : head;
  }
  return (e as Error)?.message ?? String(e);
}

export const crmApi = <T = any>(path: string, opts?: Parameters<typeof api>[1]) => api<T>(`${CRM}${path}`, opts);
